import React, { useEffect, useRef, useState } from 'react';
import {
  AlertMessage,
  ListApplicationsPageHeader,
} from './ListApplicationsPageHeader';
import { Job } from '../../../../entities/applicant_tracking/Job';
import { JobStageTemplate } from '../../../../entities/applicant_tracking/JobStageTemplate';
import ListPageEmptyState from '../../ListPageEmptyState';
import { User } from '../../../../entities/User';
import { Alert, AlertObject, AlertType } from '../../../../components/Alert';
import { HrisEmployeesService } from '../../../../services/applicant_tracking/HrisEmployeesService';
import {
  HrisEmployee,
  HrisEmployees,
} from '../../../../entities/applicant_tracking/HrisEmployees';
import { HrisProvider } from '../../../../enums/IntegrationProvider/HrisProvider';
import { EmailAccountIntegration } from '../../../../entities/EmailAccountIntegration';
import { ListApplicationsPageBody } from './ListApplicationsPageBody';
import {
  ApplicationListingFilters,
  DEFAULT_FILTER_STATE,
  FilterType,
  resetIfFilterNotSelected,
  resetPageIfFiltersChanged,
} from './ListApplicationsPageBody/ListApplicationsFilters/utils';
import {
  NewListCandidate,
  NewListCandidates,
} from '../../../../entities/v1/applicant_tracking/NewListCandidate';
import { ApplicationService } from '../../../../services/v1/applicant_tracking/ApplicationService';
import { getQueryParam } from '../../../../utils/url';
import { SEARCH_PARAM, decodeSearchQuery } from './OpenSearch';
import { FeatureFlagContext } from '../../../../contexts/FeatureFlagContext';

interface PropTypes {
  alertMessage: AlertMessage;
  atsSignableOfferEnabled: boolean;
  atsTagsPanelEnabled: boolean;
  atsApplicationFormFilterEnabled: boolean;
  atsInterviewContentFilterEnabled: boolean;
  atsJobStatusFilterEnabled: boolean;
  atsEditBeforeExportToHrisEnabled: boolean;
  atsUpdateSchedulingUserIntegrationUserInfoEnabled: boolean;
  atsBulkSelectAllApplicationsEnabled: boolean;
  currentUser: User;
  emailAccountIntegration: EmailAccountIntegration;
  hrisProvider: HrisProvider;
  jobs: Job[];
  jobStages: JobStageTemplate[];
  policies: CandidatePolicies;
  totalCandidates: number;
  offerRequisitionFlowEnabled: boolean;
}

export interface CandidatePolicies {
  addCandidateEnabled: boolean;
}

export default function ListApplicationsPage(props: PropTypes) {
  const requestsCount = useRef(0);
  const bulkSelectionRequestCount = useRef(0);
  const isInitialMount = useRef(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedCandidates, setSelectedCandidates] = useState<
    NewListCandidate[]
  >([]);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<number[] | null>(null);
  const [bulkSelectionLoading, setBulkSelectionLoading] =
    useState<boolean>(false);
  const [totalFilteredCandidates, setTotalFilteredCandidates] =
    useState<number>(0);
  const [candidates, setCandidates] = useState<NewListCandidate[]>([]);
  const [hrisEmployees, setHrisEmployees] = useState<HrisEmployee[]>(undefined);
  const [filterStates, setFilterStates] =
    useState<ApplicationListingFilters>(DEFAULT_FILTER_STATE);
  const [previousFilterStates, setPreviousFilterStates] =
    useState<ApplicationListingFilters>(DEFAULT_FILTER_STATE);
  const [filterTypes, setFilterTypes] = useState<FilterType[]>([]);
  const [alert, setAlert] = useState<AlertObject>(null);
  const [error, setError] = useState(false);
  const [filterAppliedAt, setFilterAppliedAt] = useState<string | null>(null);

  const loadCandidates = () => {
    // Decode the search parameter when reading from URL (required due to security feature)
    const encodedSearch = getQueryParam(SEARCH_PARAM);
    const decodedSearch = decodeSearchQuery(encodedSearch);

    const currentRequestCount = ++requestsCount.current;
    setIsLoading(true);

    ApplicationService.search(filterStates, decodedSearch)
      .then((response: NewListCandidates) => {
        const { candidates, entriesCount } = response;
        const applicationIds = candidates
          .map((c) => c.application?.id)
          .filter(Boolean);

        setError(false);

        if (currentRequestCount != requestsCount.current) return;

        setCandidates(candidates);
        setTotalFilteredCandidates(entriesCount);
        setFilterAppliedAt(response.filterAppliedAt);

        if (applicationIds.length > 0) {
          HrisEmployeesService.list({ applicationIds }).then(
            (response: HrisEmployees) => setHrisEmployees(response.employees),
          );
        } else {
          setHrisEmployees([]);
        }
      })
      .catch((_e) => setError(true))
      .finally(() => setIsLoading(false));
  };

  const handleSelectAll = () => {
    const encodedSearch = getQueryParam(SEARCH_PARAM);
    const decodedSearch = decodeSearchQuery(encodedSearch);
    const currentSelectAllRequest = ++bulkSelectionRequestCount.current;
    setBulkSelectionLoading(true);
    ApplicationService.listApplicationIds(
      filterStates,
      decodedSearch,
      filterAppliedAt,
    )
      .then(({ applicationIds }) => {
        if (currentSelectAllRequest !== bulkSelectionRequestCount.current)
          return;
        setBulkSelectedIds(applicationIds);
      })
      .catch(() =>
        setAlert({
          message: 'Failed to select all applications. Please try again.',
          type: AlertType.Danger,
        }),
      )
      .finally(() => setBulkSelectionLoading(false));
  };

  const handleClearBulkSelection = () => {
    setBulkSelectedIds(null);
    setSelectedCandidates([]);
  };

  const handleBulkSelectionRemove = (ids: number[]) => {
    setBulkSelectedIds((prev) => {
      if (prev === null) return null;
      const idsToRemove = new Set(ids);
      const updated = prev.filter((id) => !idsToRemove.has(id));
      if (updated.length === 0) {
        setSelectedCandidates([]);
        return null;
      }
      return updated;
    });
  };

  const handleBulkSelectionAdd = (ids: number[]) => {
    setBulkSelectedIds((prev) => {
      if (prev === null) return null;
      const existingSet = new Set(prev);
      const newIds = ids.filter((id) => !existingSet.has(id));
      return newIds.length > 0 ? [...prev, ...newIds] : prev;
    });
  };

  const performSearch = (hardRefresh: boolean) => {
    if (hardRefresh) {
      resetPageIfFiltersChanged(
        previousFilterStates,
        filterStates,
        setFilterStates,
      );
      setSelectedCandidates([]);
      setBulkSelectedIds(null);
      ++bulkSelectionRequestCount.current;
    }

    setPreviousFilterStates(filterStates);
    loadCandidates();
  };

  const reloadPage = () => {
    setSelectedCandidates([]);
    setBulkSelectedIds(null);
    loadCandidates();
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      // Only skips the first search if the search parameter is empty.
      if (!getQueryParam(SEARCH_PARAM)) {
        return;
      }
    }

    setHrisEmployees(undefined);
    performSearch(
      previousFilterStates.sortingColumn !== filterStates.sortingColumn,
    );
  }, [filterStates.sortingColumn, filterStates.page]);

  useEffect(
    () =>
      resetIfFilterNotSelected(
        filterTypes,
        props.atsTagsPanelEnabled,
        props.atsApplicationFormFilterEnabled,
        props.atsInterviewContentFilterEnabled,
        props.atsJobStatusFilterEnabled,
        setFilterStates,
      ),
    [filterTypes],
  );

  return props.totalCandidates === 0 ? (
    <ListPageEmptyState
      title={'No candidates added yet'}
      text={'Create your first candidate to start using it on your ATS.'}
      addButtonText={'Add Candidate'}
      onClickHref={'/applicant_tracking/candidate_wizard'}
      disabled={!props.policies.addCandidateEnabled}
    />
  ) : (
    <>
      {alert?.message && (
        <Alert
          type={alert.type}
          autoClearTimeout={4000}
          clearable={true}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}
      <FeatureFlagContext.Provider
        value={{
          atsUpdateSchedulingUserIntegrationUserInfoEnabled:
            props.atsUpdateSchedulingUserIntegrationUserInfoEnabled,
        }}
      >
        <ListApplicationsPageHeader
          alertMessage={props.alertMessage}
          currentUser={props.currentUser}
          candidates={candidates}
          selectedCandidates={selectedCandidates}
          bulkSelectedIds={bulkSelectedIds}
          bulkSelectionLoading={bulkSelectionLoading}
          canBulkMoveToStage={
            filterStates.jobs?.length === 1 &&
            filterStates.jobStages?.length === 1
          }
          addCandidateEnabled={props.policies.addCandidateEnabled}
          emailAccountIntegration={props.emailAccountIntegration}
          reloadPage={reloadPage}
          setAlert={setAlert}
          hrisEmployees={hrisEmployees}
          hrisProvider={props.hrisProvider}
        />
      </FeatureFlagContext.Provider>
      <FeatureFlagContext.Provider
        value={{
          atsEditBeforeExportToHrisEnabled:
            props.atsEditBeforeExportToHrisEnabled,
          atsBulkSelectAllApplicationsEnabled:
            props.atsBulkSelectAllApplicationsEnabled,
        }}
      >
        <ListApplicationsPageBody
          atsSignableOfferEnabled={props.atsSignableOfferEnabled}
          atsTagsPanelEnabled={props.atsTagsPanelEnabled}
          atsApplicationFormFilterEnabled={
            props.atsApplicationFormFilterEnabled
          }
          atsInterviewContentFilterEnabled={
            props.atsInterviewContentFilterEnabled
          }
          atsJobStatusFilterEnabled={props.atsJobStatusFilterEnabled}
          candidates={candidates}
          currentUser={props.currentUser}
          emailAccountIntegration={props.emailAccountIntegration}
          error={error}
          filterStates={filterStates}
          filterTypes={filterTypes}
          hrisEmployees={hrisEmployees}
          hrisProvider={props.hrisProvider}
          isLoading={isLoading || hrisEmployees == null}
          selectedCandidates={selectedCandidates}
          totalFilteredCandidates={totalFilteredCandidates}
          offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
          bulkSelectedIds={bulkSelectedIds}
          bulkSelectionLoading={bulkSelectionLoading}
          onSelectAll={handleSelectAll}
          onClearSelectAll={handleClearBulkSelection}
          onBulkSelectionRemove={handleBulkSelectionRemove}
          onBulkSelectionAdd={handleBulkSelectionAdd}
          onSearch={() => performSearch(true)}
          setSelectedCandidates={setSelectedCandidates}
          onFilterStatesUpdate={setFilterStates}
          setFilterTypes={setFilterTypes}
          reloadPage={reloadPage}
          setAlert={setAlert}
        />
      </FeatureFlagContext.Provider>
    </>
  );
}
